import React, { useMemo, useState } from "react";
import ActorIdentityChip from "./ActorIdentityChip";
import StateStepper from "./StateStepper";

const VARIANT_CONFIG = {
  plan: {
    submitLabel: "Submit for Approval",
    approveLabel: "Approve Plan",
    rejectLabel: "Reject Plan",
    consequence: "Approved plans may be assigned to positions.",
    steps: [
      { key: "draft", label: "Draft" },
      { key: "pending_approval", label: "Pending Approval" },
      { key: "approved", label: "Approved" },
    ],
  },
  statement: {
    submitLabel: "Release Statements",
    approveLabel: "Confirm Release",
    rejectLabel: "Reject Release",
    consequence: "Released statements are visible to recipients.",
    steps: [
      { key: "draft", label: "Draft" },
      { key: "pending_approval", label: "Pending Release" },
      { key: "released", label: "Released" },
    ],
  },
  payroll: {
    submitLabel: "Submit for Export",
    approveLabel: "Approve Export",
    rejectLabel: "Reject Export",
    consequence: "Exported batches become downstream payroll inputs.",
    steps: [
      { key: "pending_approval", label: "Pending" },
      { key: "approved", label: "Approved" },
      { key: "exported", label: "Exported" },
    ],
  },
  draw_plan: {
    submitLabel: "Submit for Approval",
    approveLabel: "Approve Draw Plan",
    rejectLabel: "Reject Draw Plan",
    consequence: "Approved draw plans may be assigned to positions. Unapproved plans cannot participate in calculation.",
    steps: [
      { key: "draft", label: "Draft" },
      { key: "pending_approval", label: "Pending Approval" },
      { key: "approved", label: "Approved" },
    ],
  },
  split_plan: {
    submitLabel: "Submit for Approval",
    approveLabel: "Approve Split Plan",
    rejectLabel: "Reject Split Plan",
    consequence: "Approved split plans may be assigned to positions. Unapproved plans cannot participate in calculation.",
    steps: [
      { key: "draft", label: "Draft" },
      { key: "pending_approval", label: "Pending Approval" },
      { key: "approved", label: "Approved" },
    ],
  },
  spiff_plan: {
    submitLabel: "Submit for Approval",
    approveLabel: "Approve SPIFF Plan",
    rejectLabel: "Reject SPIFF Plan",
    consequence: "Approved SPIFF plans may be assigned to positions. Unapproved plans cannot participate in calculation.",
    steps: [
      { key: "draft", label: "Draft" },
      { key: "pending_approval", label: "Pending Approval" },
      { key: "approved", label: "Approved" },
    ],
  },
  spiff_award: {
    submitLabel: "Submit Award for Approval",
    approveLabel: "Approve Award",
    rejectLabel: "Reject Award",
    consequence: "Approved awards are included in the next calculation run.",
    steps: [
      { key: "draft", label: "Draft" },
      { key: "pending_approval", label: "Pending Approval" },
      { key: "approved", label: "Approved" },
    ],
  },
};

function completedStepsFor(config, currentState) {
  const index = config.steps.findIndex((step) => step.key === currentState);
  if (index <= 0) return [];
  return config.steps.slice(0, index).map((step) => step.key);
}

export default function ApprovalPanel({
  variant,
  currentState,
  submittedBy,
  currentUserId,
  onSubmit,
  onApprove,
  onReject,
  rejectionReason,
  approvedBy,
  approvedAt,
  loading = false,
}) {
  const [reason, setReason] = useState("");
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.plan;

  const isPending = currentState === "pending_approval";
  const isSubmitter = currentUserId === submittedBy;
  const selfApprovalBlocked = isPending && isSubmitter;

  const completedSteps = useMemo(
    () => completedStepsFor(config, currentState),
    [config, currentState]
  );

  const canSubmit =
    currentState === "draft" ||
    (variant === "payroll" &&
      currentState === "pending_approval" &&
      !submittedBy);

  const canApprove = isPending && !selfApprovalBlocked;
  const canReject = isPending && reason.trim().length > 0;

  return (
    <section className="space-y-5 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-5">
      <div>
        <h2 className="text-tok-lg font-semibold text-[rgb(var(--color-text))]">
          Approval Workflow
        </h2>
        <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Two-actor approval is required for this workflow.
        </p>
      </div>

      <StateStepper
        steps={config.steps}
        currentStep={currentState}
        completedSteps={completedSteps}
      />

      <div className="rounded-md border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4">
        <div className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
          Current State
        </div>
        <div className="mt-1 text-tok-sm font-semibold text-[rgb(var(--color-text))]">
          {config.steps.find((step) => step.key === currentState)?.label ||
            currentState}
        </div>

        {submittedBy && (
          <div className="mt-3">
            <ActorIdentityChip
              name={submittedBy}
              role="submitter"
              roleLabel="Submitter"
            />
          </div>
        )}

        {approvedBy && (
          <div className="mt-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Approved by{" "}
            <span className="font-medium text-[rgb(var(--color-text))]">
              {approvedBy}
            </span>
            {approvedAt && <> · {new Date(approvedAt).toLocaleString()}</>}
          </div>
        )}

        {rejectionReason && (
          <div className="mt-4 rounded-md border border-[rgb(var(--color-warning))] bg-[rgb(var(--color-surface))] p-3">
            <div className="text-tok-sm font-medium text-[rgb(var(--color-warning))]">
              Last Rejection Reason
            </div>
            <div className="mt-1 text-tok-sm text-[rgb(var(--color-text))]">
              {rejectionReason}
            </div>
          </div>
        )}
      </div>

      {isPending && (
        <div className="space-y-2">
          <label className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Rejection Reason
          </label>
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className="min-h-24 w-full rounded-md border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))]"
            placeholder="Required before rejecting."
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {canSubmit && onSubmit && (
          <button
            type="button"
            disabled={loading}
            onClick={onSubmit}
            className="rounded-md bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {config.submitLabel}
          </button>
        )}

        {selfApprovalBlocked && (
          <div className="rounded-md border border-[rgb(var(--color-warning))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm font-medium text-[rgb(var(--color-warning))]">
            Approval unavailable — submitter cannot approve their own request.
          </div>
        )}

        {canApprove && onApprove && (
          <button
            type="button"
            disabled={loading}
            onClick={onApprove}
            className="rounded-md bg-[rgb(var(--color-success))] px-4 py-2 text-tok-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {config.approveLabel}
          </button>
        )}

        {isPending && onReject && (
          <button
            type="button"
            disabled={loading || !canReject}
            onClick={() => onReject(reason.trim())}
            className="rounded-md border border-[rgb(var(--color-danger))] bg-[rgb(var(--color-surface))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-surface-muted))] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {config.rejectLabel}
          </button>
        )}
      </div>

      <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
        {config.consequence}
      </p>
    </section>
  );
}
